import { ROUTES } from "@src/common/constants";

export const APP_ROUTE_NAMES = [
	"home",
	"library",
	"whiteboards",
	"whiteboard",
	"trash",
	"draft",
	"profile",
	"card",
	"unknown",
] as const;

export type AppRouteName = (typeof APP_ROUTE_NAMES)[number];

export type ParsedAppRoute = {
	pathname: string;
	search: string;
	route: AppRouteName;
	whiteboardId?: string;
	cardId?: string;
};

const RESERVED_FIRST_SEGMENTS = new Set(
	[
		ROUTES.whiteboards,
		ROUTES.trash,
		ROUTES.flowWhiteboard,
		ROUTES.draft,
		ROUTES.library,
		ROUTES.home,
		ROUTES.profile,
		ROUTES.callback,
		ROUTES.authEmailVerified,
		ROUTES.invite,
		ROUTES.checkoutReturn,
		ROUTES.default,
		ROUTES.cliLogin,
	].map((path) => path.replace(/^\//, "").split("/")[0]),
);

function stripTrailingSlash(pathname: string) {
	if (pathname.length > 1 && pathname.endsWith("/")) {
		return pathname.slice(0, -1);
	}
	return pathname;
}

/**
 * Map a React Router location to a stable agent-facing route name.
 * Only authenticated app destinations are recognized; everything else
 * is `unknown` (never treat auth/public paths as card pages).
 */
export function parseAppRoute(pathname: string, search = ""): ParsedAppRoute {
	const normalized = stripTrailingSlash(pathname);
	const base: ParsedAppRoute = {
		pathname: normalized,
		search,
		route: "unknown",
	};

	if (normalized === ROUTES.home) {
		return { ...base, route: "home" };
	}
	if (normalized === ROUTES.library) {
		return { ...base, route: "library" };
	}
	if (normalized === ROUTES.whiteboards) {
		return { ...base, route: "whiteboards" };
	}
	if (normalized === ROUTES.trash) {
		return { ...base, route: "trash" };
	}
	if (normalized === ROUTES.profile) {
		return { ...base, route: "profile" };
	}
	if (
		normalized === ROUTES.draft ||
		normalized.startsWith(`${ROUTES.draft}/`)
	) {
		return { ...base, route: "draft" };
	}

	const whiteboardPrefix = `${ROUTES.whiteboards}/`;
	if (normalized.startsWith(whiteboardPrefix)) {
		const whiteboardId = normalized.slice(whiteboardPrefix.length);
		if (whiteboardId.length > 0 && !whiteboardId.includes("/")) {
			return { ...base, route: "whiteboard", whiteboardId };
		}
		return base;
	}

	if (normalized.startsWith("/") && !normalized.slice(1).includes("/")) {
		const cardId = normalized.slice(1);
		if (cardId.length > 0 && !RESERVED_FIRST_SEGMENTS.has(cardId)) {
			return { ...base, route: "card", cardId };
		}
	}

	return base;
}
